import { Alert, Skeleton } from '@chakra-ui/react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';
import { DbGraphUtils } from '@finley-technologies/tape-builder';

import {
  DirectKeep,
  DirectUpload,
  useDirectUpload,
} from 'packages/dataPipeline/hooks/useDirectUpload';
import { useLatestMeshDocumentMetadata } from 'packages/dataPipeline/hooks/useLatestMeshDocumentMetadata';
import { useMeshDocument } from 'packages/dataPipeline/hooks/useMeshDocument';
import { useGetMaybeCompleteMeshRunRecordQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { MeshRunParams } from 'packages/dataPipeline/types/dataPipelineTypes';

// monitors the upload process and provides following values.
export type UploadContext = {
  meshRunId: DataPipeline.MeshRunId | null; // meshRunId will be null if the pipeline has not started yet.
  instances: DataPipeline.Instance[]; // instances will be empty if the pipeline has not started yet.
  completeRecord: DataPipeline.CompleteMeshRunRecord | null; // will be null if the pipeline has not started yet.
  canUpload: boolean; // if we can upload a new instance.
  meshDocument: DataPipeline.MeshDocument;
  meshRunParams: MeshRunParams;
  // non-null while the run's version is new and not yet started: an upload starts
  // it (rejected if someone else just did), then this hears that it has. null when
  // the run exists: uploads only fill it in.
  onRunStarted: (() => void) | null;
  // non-null while the run's version is new: call it once an input was sent that
  // starts the run without a submission (a cash balance). Until the polled run
  // shows up, `runStarting` holds the other uploads back, since a submission
  // would still claim to start a version that now exists and be rejected.
  onRunStarting: (() => void) | null;
  runStarting: boolean;
  // call once an upload was attempted, sent or failed: refetches now and keeps
  // polling the run, however long ago it last changed.
  onUploadAttempted: () => void;
};

// how often a starting run is polled for, and how long until the drawer gives up
// waiting for it and lets the uploads try to start it themselves.
const RUN_STARTING_POLL_MS = 1000;
const RUN_STARTING_TIMEOUT_MS = 60 * 1000;

const uploadContext = createContext<UploadContext | null>(null);

export const useUploadContext = (): UploadContext => {
  const context = useContext(uploadContext);
  finleyAssert(
    context,
    'useUploadMonitor must be used within a uploadContext.',
  );
  return context;
};

// describes sanitization status of a mesh input.
// total: how many sanitize steps are configured.
// success: how many sanitize steps have succeeded.
// failure: how many sanitize steps have failed.
export type Sanitized = {
  total: number;
  success: number;
  failure: number;
};

export const useUploadContextMeshInputs = (): DataPipeline.MeshInput[] => {
  const context = useUploadContext();
  return context.meshDocument.meshInputs;
};

export const useUploadContextMeshDocument = (): DataPipeline.MeshDocument => {
  const context = useUploadContext();
  return context.meshDocument;
};

export const useUploadContextArtifactInstance = (
  artifactId: string,
): {
  instance: DataPipeline.Instance | null;
  directUpload: DirectUpload;
  directKeep: DirectKeep;
  sanitized: Sanitized;
  onRunStarting: (() => void) | null;
  runStarting: boolean;
  onUploadAttempted: () => void;
} => {
  const context = useUploadContext();
  const { meshRunParams } = context;
  const instanceFound: DataPipeline.Instance | null = useMemo(() => {
    return (
      context.instances.find(
        (instance) => instance.artifactId === artifactId,
      ) ?? null
    );
  }, [context.instances, artifactId]);

  // for some tape artifacts we may wait for another artifact
  // for sanitization complete.
  const sanitized: Sanitized = useMemo(() => {
    const foundMeshInput = context.meshDocument.meshInputs.find((meshInput) => {
      return meshInput.artifactId === artifactId;
    });

    finleyAssert(foundMeshInput, `meshInput not found: ${artifactId}`);

    const total = foundMeshInput.sanitizedArtifactIds.length;
    const instances = context.instances.filter((instance) =>
      foundMeshInput.sanitizedArtifactIds.includes(instance.artifactId),
    );

    const success = instances.filter(({ status }) => status === 'success');
    const error = instances.filter(({ status }) => status === 'error');

    return {
      total,
      success: success.length,
      failure: error.length,
    };
  }, [context.meshDocument.meshInputs, context.instances, artifactId]);

  const { meshDocument } = context;
  const slugOf = useCallback(
    (id: string): string => {
      const artifact = meshDocument.artifacts.find(
        (candidate) => candidate.id === id,
      );
      finleyAssert(artifact, `artifact not found in mesh: ${id}`);
      return artifact.artifactSlug;
    },
    [meshDocument.artifacts],
  );
  const meshInput = meshDocument.meshInputs.find(
    (candidate) => candidate.artifactId === artifactId,
  );
  const { directUpload, directKeep } = useDirectUpload({
    implementationId: meshDocument.implementationId,
    artifactSlug: slugOf(artifactId),
    fileExtension: meshInput?.type === 'file' ? meshInput.extension : null,
    rdfv: meshRunParams.rdfv,
    onRunStarted: context.onRunStarted,
  });

  return {
    directUpload,
    directKeep,
    instance: instanceFound,
    sanitized,
    onRunStarting: context.onRunStarting,
    runStarting: context.runStarting,
    onUploadAttempted: context.onUploadAttempted,
  };
};

export const useUploadContextMeshRunParams = (): MeshRunParams => {
  const context = useUploadContext();
  return context.meshRunParams;
};

export const UploadContextProvider = ({
  children,
  meshRunId,
  meshRunParams,
  organizationId,
  poll,
  onRunStarted,
}: {
  children: React.ReactNode;
  organizationId: string;
  meshRunId: DataPipeline.MeshRunId | null;
  meshRunParams: MeshRunParams | null;
  // see UploadContext.onRunStarted
  onRunStarted: (() => void) | null;
  // whether this provider polls the run. Providers of the same run share the
  // Apollo cache, so only one of them needs to poll.
  poll: boolean;
}) => {
  finleyAssert(
    meshRunId || meshRunParams,
    'meshRunId or meshRunParams must be provided',
  );
  const {
    latestMeshDocumentMetadata,
    loading: latestMeshDocumentMetadataLoading,
    error: latestMeshDocumentMetadataError,
  } = useLatestMeshDocumentMetadata({ organizationId });
  const gqlMeshRunParams = meshRunParams
    ? {
        meshDocumentId: meshRunParams.meshDocumentMetadata.meshDocumentId,
        reportingDate: meshRunParams?.rdfv.reportingDate,
        fileVersion: meshRunParams?.rdfv.fileVersion,
      }
    : null;
  const {
    data,
    error: meshRunRecordError,
    refetch,
    startPolling,
    stopPolling,
  } = useGetMaybeCompleteMeshRunRecordQuery({
    variables: {
      meshRunId,
      meshRunParams: gqlMeshRunParams,
    },
  });

  const serializedCompleteMeshRunRecord = data?.maybeCompleteMeshRunRecord;

  // when an upload was last attempted from this view; its run is polled from
  // then on.
  const [uploadAttemptedAt, setUploadAttemptedAt] = useState<Date | null>(null);
  const onUploadAttempted = useCallback((): void => {
    setUploadAttemptedAt(new Date());
    refetch();
  }, [refetch]);

  // set from an input that starts the run without a submission, until the run
  // shows up (or the wait times out).
  const [runStarting, setRunStarting] = useState<boolean>(false);
  const onRunStarting = useMemo(
    (): (() => void) | null =>
      onRunStarted ? (): void => setRunStarting(true) : null,
    [onRunStarted],
  );
  const runExists = !!serializedCompleteMeshRunRecord;
  useEffect(() => {
    if (!runStarting) {
      return undefined;
    }
    if (runExists) {
      setRunStarting(false);
      onRunStarted?.();
      return undefined;
    }
    const timeout = setTimeout(
      () => setRunStarting(false),
      RUN_STARTING_TIMEOUT_MS,
    );
    return (): void => clearTimeout(timeout);
  }, [onRunStarted, runExists, runStarting]);

  // When the complete run record is available it already carries the full mesh
  // document, so `selectedMeshDocument` is unused. Skip the lazy-load fetch (and
  // its loading gate) in that case to avoid an unnecessary request and delay.
  const {
    meshDocument: selectedMeshDocument,
    loading: selectedMeshDocumentLoading,
    error: selectedMeshDocumentError,
  } = useMeshDocument(
    serializedCompleteMeshRunRecord
      ? null
      : meshRunParams?.meshDocumentMetadata.meshDocumentId ?? null,
  );
  const contextValue: UploadContext | null = useMemo(() => {
    if (!serializedCompleteMeshRunRecord) {
      if (!meshRunParams || !selectedMeshDocument) {
        // when hosted on pipeline run details page. meshRunParams will be null
        // we will have to wait for the meshRunParams from server.
        return null;
      }

      const canUpload = !!latestMeshDocumentMetadata.find(
        (latestDoc) =>
          latestDoc.meshDocumentId ===
          meshRunParams.meshDocumentMetadata.meshDocumentId,
      );

      // when hosted on pipeline details page or on home page
      // we do not wait for server, as we know the meshRunParams
      return {
        meshRunId: null,
        completeRecord: null,
        instances: [],
        canUpload,
        meshDocument: selectedMeshDocument,
        meshRunParams,
        onRunStarted,
        onRunStarting,
        runStarting,
        onUploadAttempted,
      };
    }
    const completeRecord = DbGraphUtils.deserializeCompleteMeshRunRecord(
      serializedCompleteMeshRunRecord,
    );

    const { instances } = completeRecord;

    const canUpload = !!latestMeshDocumentMetadata.find(
      (latestDoc) =>
        latestDoc.meshDocumentId === completeRecord.meshDocument.meshDocumentId,
    );

    return {
      completeRecord,
      instances,
      meshRunId: completeRecord.meshRunRecord.meshRunId,
      canUpload,
      meshDocument: completeRecord.meshDocument,
      meshRunParams: {
        meshDocumentMetadata: DataPipeline.toMeshDocumentMetadata(
          completeRecord.meshDocument,
        ),
        rdfv: {
          reportingDate: completeRecord.meshRunRecord.reportingDate,
          fileVersion: completeRecord.meshRunRecord.fileVersion,
        },
      },
      onRunStarted,
      onRunStarting,
      runStarting,
      onUploadAttempted,
    };
  }, [
    latestMeshDocumentMetadata,
    meshRunParams,
    onRunStarted,
    onRunStarting,
    onUploadAttempted,
    runStarting,
    selectedMeshDocument,
    serializedCompleteMeshRunRecord,
  ]);

  const pipelineLastUpdated =
    contextValue?.completeRecord?.meshRunRecord.updatedAt ?? null;

  const pipelineStatus =
    contextValue?.completeRecord?.meshRunRecord.status ?? null;

  const errorMessage =
    latestMeshDocumentMetadataError?.message ??
    selectedMeshDocumentError?.message ??
    meshRunRecordError?.message;

  useEffect(() => {
    // we do not want to poll for older pipelines: one is recent if it was last
    // updated, or a file was sent to it from here, less than 2 hours ago.
    const TWO_HOURS = 2 * 60 * 60 * 1000;
    const isRecent = (date: Date | null): boolean =>
      !!date && Date.now() - date.getTime() < TWO_HOURS;
    const isRecentPipeline =
      !pipelineLastUpdated ||
      isRecent(pipelineLastUpdated) ||
      isRecent(uploadAttemptedAt);

    const shouldPoll =
      poll &&
      !errorMessage && // do not poll if there is an error fetching the pipeline.
      isRecentPipeline && // do not poll for older pipelines.
      pipelineStatus !== 'success'; // do not poll if pipeline successfully completed.

    if (shouldPoll) {
      startPolling(runStarting ? RUN_STARTING_POLL_MS : 5000);
    } else {
      stopPolling();
    }
  }, [
    errorMessage,
    uploadAttemptedAt,
    pipelineLastUpdated,
    pipelineStatus,
    poll,
    runStarting,
    startPolling,
    stopPolling,
  ]);

  if (errorMessage) {
    return <Alert>LatestDocument Error: {errorMessage} </Alert>;
  }

  if (
    !contextValue ||
    latestMeshDocumentMetadataLoading ||
    selectedMeshDocumentLoading
  ) {
    return <Skeleton width="100%" height="400px" />;
  }

  return (
    <uploadContext.Provider value={contextValue}>
      {contextValue && children}
    </uploadContext.Provider>
  );
};
